import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff } from 'lucide-react';
import { checkLogin, change_MyData, setOtpVerified } from '../../../Common/store';
import { useToast } from '../../../Common/context/ToastContext';
import { useTooltip } from '../../../Common/context/TooltipContext';
import { cpLogin } from '../../api/cp';
import { getOneHourExpiry } from '../../../Common/utils/sessionExpiry';
import { getLoginBackgroundAssetUrl, getLoginLogoAssetUrl, getLoginLogoStyle } from '../../../Common/constants/adminImages';

const SESSION_EXPIRY_KEY = 'session_expiry';
const CP_SIGNUP_URL = (process.env.REACT_APP_CP_SIGNUP_URL || '').trim();

/**
 * 가맹점 로그인 - 중앙 카드 형태, 배경 이미지
 */
function MerchantLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { resetShowAll } = useTooltip();

  const [cpId, setCpId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    resetShowAll();
  }, [resetShowAll]);

  // 로그인 페이지에서 스크롤바 제거
  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cpId.trim() || !password.trim()) {
      showToast('아이디와 비밀번호를 입력해 주세요.');
      return;
    }
    try {
      setLoading(true);
      const data = await cpLogin({ cpId: cpId.trim(), password });

      // 응답: cp(id, cp_id, name, status), must_change_password
      const cp = data?.cp ?? data;
      const name = cp?.name || '가맹점관리자';

      dispatch(checkLogin(true));
      dispatch(setOtpVerified(true)); // 가맹점은 OTP 건너뛰기
      dispatch(
        change_MyData({
          name,
          type: 'merchant',
          cpId: cp?.cp_id ?? cp?.id ?? cpId.trim(),
          status: cp?.status,
        }),
      );

      // 세션 만료 시간 설정 (60분)
      const expiry = String(getOneHourExpiry());
      try {
        sessionStorage.setItem(SESSION_EXPIRY_KEY, expiry);
        localStorage.setItem('ls_session_expiry', expiry);
      } catch (err) { }

      // OTP 없이 바로 정산달력으로 이동
      navigate('/cp/integrated-settlement');
    } catch (e) {
      showToast(e.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex justify-center items-center z-10 overflow-hidden bg-[#fff]">
      {/* 배경 이미지 - 1920px */}
      <div
        className="absolute top-0 bottom-0 bg-cover bg-center bg-no-repeat"
        style={{
          width: 'min(100vw, 1920px)',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundImage: `url('/img/login_bg_img1.png')`,
          // backgroundImage: `url(${getLoginBackgroundAssetUrl()})`,
        }}
      />

      {/* 좌측 상단 텍스트 */}

      {/* 중앙 로그인 카드 */}
      <div className="relative z-10 w-[500px] bg-white rounded-2xl shadow-xl p-10 mx-4">
        <div className="flex justify-center">
          <div className="rounded-xl flex items-center justify-center">
            <img src={getLoginLogoAssetUrl()} alt="" className="h-12 object-contain" style={getLoginLogoStyle({ marginBottom: '10px' })} />
          </div>
        </div>
        <div className="text-white text-[14px] font-medium z-20 mb-8" style={{ color: '#000', textAlign: 'center' }}>
          가맹점페이지
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[14px] font-bold text-[#191f28] mb-2">아이디</label>
            <input
              type="text"
              value={cpId}
              onChange={(e) => setCpId(e.target.value)}
              placeholder="가맹점 ID (cpId)"
              className="w-full px-4 py-3 rounded-lg border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[#0064ff] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-[14px] font-bold text-[#191f28] mb-2">비밀번호</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                className="w-full px-4 py-3 pr-12 rounded-lg border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[#0064ff] focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b95a1] hover:text-[#191f28] p-1 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 text-white rounded-lg font-bold text-[16px] transition-colors hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: '#191f28' }}
          >
            {loading ? '로그인 중...' : '로그인'}
          </button>
          {CP_SIGNUP_URL && (
            <a
              href={CP_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3.5 bg-white text-[#191f28] border border-[#191f28] rounded-lg font-bold text-[16px] text-center transition-colors hover:bg-[#f2f4f6]"
            >
              회원가입
            </a>
          )}
        </form>
        <div
          style={{
            position: 'absolute',
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <p style={{ fontSize: '12px', color: '#000', margin: 0 }}>
            Copyright 2026 nToss-Pharm. All rights reserved.
          </p>
        </div>
        {/* <div className="mt-8 pt-6 border-t border-[#f2f4f6] text-center">
          <Link to="/admin" className="text-[14px] font-medium text-[#8b95a1] hover:text-[#0064ff] transition-colors">
            관리자 로그인
          </Link>
        </div> */}
      </div>

      {/* 하단 로고 */}
      {/* <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <img src="/img/ntoss_logo_black.png" alt="ntoss" className="h-8 object-contain" />
      </div> */}
    </div>
  );
}

export default MerchantLogin;
